import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  ArrowRight, 
  ShieldCheck, 
  Zap, 
  Globe, 
  BarChart3, 
  Cpu, 
  Layers,
  Activity,
  Target,
  Rocket,
  Building2,
  Shield,
  Search,
  CheckCircle2,
  Workflow,
  History,
  Bot,
  MessageSquare,
  TrendingUp,
  MapPin,
  Lock,
  ChevronRight,
  LogOut
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AxiomFlowMark } from './shared/BrandLogo';

// ============== Styled Components ==============
const Footer = styled.footer`
  padding: 4rem 2rem;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: #020617;
  color: white;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Section = styled.section`
  padding: 6rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

const HeroSection = styled(Section)`
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 100px;
  color: #60a5fa;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 900;
  color: white;
  line-height: 0.95;
  letter-spacing: -0.04em;
  margin-bottom: 2rem;
  
  span {
    background: linear-gradient(to right, #60a5fa, #a78bfa, #f472b6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.125rem, 3vw, 1.5rem);
  color: #94a3b8;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 3.5rem;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 2.5rem;
  width: 100%;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 32px;
  padding: 3.5rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: rgba(15, 23, 42, 0.6);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-8px);
    box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.5);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.color || 'linear-gradient(to right, #3b82f6, #8b5cf6)'};
    opacity: 0.6;
  }
`;

const IconWrapper = styled.div<{ bgColor: string }>`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 2rem;
  box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.3);
`;

const ProductTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
`;

const TargetAudience = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Description = styled.p`
  color: #94a3b8;
  line-height: 1.7;
  font-size: 1.125rem;
  margin-bottom: 2.5rem;
  flex-grow: 1;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 3rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #e2e8f0;
  font-size: 0.9rem;
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
    color: #10b981;
    flex-shrink: 0;
  }
`;

const EnterButton = styled.button<{ color: string }>`
  width: 100%;
  padding: 1.25rem;
  background: ${props => props.color};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s;
  
  &:hover {
    filter: brightness(1.1);
    transform: scale(1.02);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 6rem 0;
  width: 100%;
  
  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
`;

const StatNumber = styled.div`
  font-size: 3.5rem;
  font-weight: 900;
  background: linear-gradient(to right, #60a5fa, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const MarketingSection = styled(Section)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    color: #3b82f6;
  }
`;

const SectionDesc = styled.p`
  color: #94a3b8;
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto;
`;

const FeatureCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 2.5rem;
  transition: all 0.3s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const FeatureCardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const FeatureCardDesc = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 100;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #f87171;
    border-color: rgba(239, 68, 68, 0.2);
  }
`;

const UserBranding = styled.div`
  position: absolute;
  top: 2rem;
  left: 2rem;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 100;
`;

const AppLogo = styled(AxiomFlowMark).attrs({ size: 40 })`
  box-shadow: 0 10px 20px -5px rgba(59, 130, 246, 0.3);
`;

const AppName = styled.div`
  .main {
    font-size: 16px;
    font-weight: 800;
    color: white;
    letter-spacing: -0.02em;
  }
  .sub {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleOMSClick = () => {
    navigate('/home');
  };

  const handleCopilotClick = () => {
    const copilotUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:3001' 
      : `${window.location.protocol}//copilot.${window.location.host.split('.').slice(1).join('.')}`;
    window.location.href = copilotUrl;
  };

  return (
    <PageContainer>
      <UserBranding>
        <AppLogo />
        <AppName>
          <div className="main">AxiomFlow</div>
          <div className="sub">{user?.tenantName ? `${user.tenantName} Portal` : 'Institutional Portal'}</div>
        </AppName>
      </UserBranding>

      <LogoutButton onClick={logout}>
        <LogOut className="w-4 h-4" />
        Sign Out
      </LogoutButton>

      {/* Dynamic background accents */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[10%] left-[-10%] w-[60%] h-[60%] bg-blue-600/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[10%] right-[-10%] w-[60%] h-[60%] bg-violet-600/5 blur-[120px] rounded-full" />
        <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] bg-indigo-600/5 blur-[100px] rounded-full" />
      </div>

      <HeroSection>
        <motion.div variants={staggerContainer} initial="initial" animate="animate">
          <Badge variants={fadeInUp}>
            <ShieldCheck className="w-4 h-4" />
            Authorized Institutional Gateway
          </Badge>

          <Title variants={fadeInUp}>
            AxiomFlow <span>Platform</span>
          </Title>

          <Subtitle variants={fadeInUp}>
            Welcome to the centralized portal for your AxiomFlow trading,
            portfolio management, and operations workspace.
          </Subtitle>

          <CardGrid>
            {/* Full OMS Product */}
            <ProductCard 
              variants={fadeInUp} 
              color="linear-gradient(to right, #3b82f6, #2563eb)"
            >
              <IconWrapper bgColor="linear-gradient(135deg, #3b82f6, #2563eb)">
                <Layers className="w-8 h-8" />
              </IconWrapper>
              
              <TargetAudience>
                <Target className="w-3 h-3" />
                Mid-Market Leaders • $500M - $50B AUM
              </TargetAudience>
              
              <ProductTitle>Axiom OMS</ProductTitle>
              
              <Description>
                A comprehensive end-to-end platform for "missing middle" managers. 
                Full IBOR, trade execution, and automated middle office operations 
                in one unified workspace.
              </Description>
              
              <FeatureGrid>
                <FeatureItem><CheckCircle2 /> Multi-Asset Execution</FeatureItem>
                <FeatureItem><CheckCircle2 /> Real-time IBOR</FeatureItem>
                <FeatureItem><CheckCircle2 /> Compliance Engine</FeatureItem>
                <FeatureItem><CheckCircle2 /> RE & Private Assets</FeatureItem>
                <FeatureItem><CheckCircle2 /> Recon & Middle Office</FeatureItem>
                <FeatureItem><CheckCircle2 /> Regulatory Reports</FeatureItem>
              </FeatureGrid>
              
              <EnterButton color="#3b82f6" onClick={handleOMSClick}>
                Enter OMS Platform
                <ArrowRight className="w-5 h-5" />
              </EnterButton>
            </ProductCard>

            {/* OMS CoPilot Product */}
            <ProductCard 
              variants={fadeInUp} 
              color="linear-gradient(to right, #a78bfa, #8b5cf6)"
            >
              <IconWrapper bgColor="linear-gradient(135deg, #a78bfa, #8b5cf6)">
                <Cpu className="w-8 h-8" />
              </IconWrapper>
              
              <TargetAudience>
                <Rocket className="w-3 h-3" />
                Enterprise Giants • $50B+ AUM
              </TargetAudience>
              
              <ProductTitle>AxiomFlow Copilot</ProductTitle>
              
              <Description>
                The AI-native sidecar for enterprise operations. Sits alongside 
                legacy systems to provide modern intelligence, 
                enhanced automation, and real-time agility.
              </Description>
              
              <FeatureGrid>
                <FeatureItem><Zap /> Intent-First Command</FeatureItem>
                <FeatureItem><Zap /> Liquidity Patrol</FeatureItem>
                <FeatureItem><Zap /> Shadow Compliance</FeatureItem>
                <FeatureItem><Zap /> Smart Workflows</FeatureItem>
                <FeatureItem><Zap /> Legacy Enhancer</FeatureItem>
                <FeatureItem><Zap /> Multi-LLM Support</FeatureItem>
              </FeatureGrid>
              
              <EnterButton color="#8b5cf6" onClick={handleCopilotClick}>
                Launch AI CoPilot
                <ArrowRight className="w-5 h-5" />
              </EnterButton>
            </ProductCard>
          </CardGrid>

          <div className="mt-12 text-slate-600 text-[10px] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-4">
            <div className="h-px w-8 bg-slate-800" />
            Institutional Grade Infrastructure
            <div className="h-px w-8 bg-slate-800" />
          </div>
        </motion.div>
      </HeroSection>

      <Footer>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-center gap-8 text-slate-600 text-[10px] font-bold uppercase tracking-widest">


</div>
          <p>© 2026 AxiomFlow • Institutional grade trading infrastructure</p>
        </div>
      </Footer>
    </PageContainer>
  );
};

export default LandingPage;
